import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import {
  DEFAULT_WISHLIST_POLICY,
  type WishlistPolicyPayload,
  type WishlistPolicySettings,
} from '../types/shoppingPolicy';

/**
 * 관심상품/간편구매 정책 GET/PUT — user-service admin(고도몰 ID51_3 "관심상품/간편구매 설정").
 * 게이트웨이 admin-catchall → user-service(rewrite 없음). 경로 prefix 가 cart(order) 와 다르다.
 * v2 envelope `{ success, data, error, meta }`. PUT 은 Idempotency-Key 를 서명 일관성(cart/order
 * 와 동일 형태)을 위해 전송한다. 단, 이 PUT 은 싱글톤 설정의 자연 멱등 upsert 라(같은 키+payload
 * → 같은 결과) user-service 가 헤더를 수용하되 409 충돌검사는 하지 않는다(order/cart 의 인-서비스
 * IdempotencyService 와 달리 user-service 엔 공용 멱등 계층이 없음). 즉 키는 무해하며 동작 불변.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 `adminFetch`(401 자동복구 fetch)로 호출한다
 * (cart 와 동일 패턴). adminHeaders + credentials 는 adminFetch 내부에서 부착.
 */
const PATH = '/admin/v1/wishlist/policy-settings';

interface WishlistEnvelope {
  success: boolean;
  data?: WishlistPolicySettings | null;
}

/**
 * 응답을 정규화한다 — BE 가 일부 필드를 누락/널로 줄 수 있어 기본값으로 채워 폼이 항상
 * controlled 상태를 유지하도록 한다(undefined → uncontrolled 경고 방지).
 */
const normalize = (
  s: WishlistPolicySettings | null | undefined,
): WishlistPolicySettings => ({
  wishlistEnabled: s?.wishlistEnabled ?? DEFAULT_WISHLIST_POLICY.wishlistEnabled,
  guestWishlistEnabled:
    s?.guestWishlistEnabled ?? DEFAULT_WISHLIST_POLICY.guestWishlistEnabled,
  wishlistMaxItems: s?.wishlistMaxItems ?? DEFAULT_WISHLIST_POLICY.wishlistMaxItems,
  quickbuyEnabled: s?.quickbuyEnabled ?? DEFAULT_WISHLIST_POLICY.quickbuyEnabled,
  updatedBy: s?.updatedBy ?? null,
  updatedAt: s?.updatedAt ?? null,
});

export const wishlistPolicyQueryKey = () =>
  ['admin', 'wishlist-policy-settings'] as const;

const fetchSettings = async (): Promise<WishlistPolicySettings> => {
  const res = await adminFetch(PATH);
  if (!res.ok) throw new Error(`wishlist policy settings fetch failed: ${res.status}`);
  const env = (await res.json()) as WishlistEnvelope;
  return normalize(env.data);
};

export const useWishlistPolicyQuery = () =>
  useQuery<WishlistPolicySettings>({
    queryKey: wishlistPolicyQueryKey(),
    queryFn: fetchSettings,
  });

export const useWishlistPolicyMutation = () => {
  const qc = useQueryClient();
  return useMutation<WishlistPolicySettings, Error, WishlistPolicyPayload>({
    mutationFn: async (payload) => {
      const res = await adminFetch(PATH, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', PATH, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`wishlist policy settings update failed: ${res.status}`);
      const env = (await res.json()) as WishlistEnvelope;
      return normalize(env.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(wishlistPolicyQueryKey(), data);
      void qc.invalidateQueries({ queryKey: wishlistPolicyQueryKey() });
    },
  });
};
